import axios from 'axios';
import { readStoredUser } from '../utils/safeUser';
import {
  domainsForMutationUrl,
  notifyDataChanged,
  type DataDomain,
} from '../utils/dataSync';

/**
 * Base URL for the API. Set at build time (Create React App: REACT_APP_*).
 * - Production (Docker + nginx proxy): omit or use "/api" — same origin as the SPA.
 * - Local dev hitting API directly: REACT_APP_API_URL=http://localhost:5001/api in .env.local
 */
const baseURL = (process.env.REACT_APP_API_URL || '/api').replace(/\/$/, '');

const api = axios.create({
  baseURL,
});

api.interceptors.request.use((config) => {
  const user = readStoredUser();
  if (user) {
    if (user.role != null) config.headers['x-user-role'] = String(user.role);
    if (user.id != null) config.headers['x-user-id'] = String(user.id);
    if (typeof user.username === 'string' && user.username.trim()) {
      config.headers['x-user-username'] = user.username.trim();
    }
    if (typeof user.full_name === 'string' && user.full_name) {
      config.headers['x-user-name'] = user.full_name;
    }
  }
  return config;
});

// ── In-memory GET cache (per browser tab) ───────────────────────────────────
// Short TTLs + mutation invalidation keep Inventory/Finance feeling live.
// Stale-while-revalidate serves last payload instantly while a background GET refreshes.
const CACHE_DURATION = 5 * 60 * 1000; // 5m — static reference data
const SHORT_CACHE_DURATION = 25 * 1000; // 25s — inventory / orders / production lists
const MUTABLE_LIST_CACHE_DURATION = 20 * 1000; // 20s — expenses / procurements lists
const PURCHASE_HUB_CACHE_DURATION = 25 * 1000; // 25s — purchase hub (was 90s; caused stale PRs)
const FINANCE_KPI_CACHE_DURATION = 20 * 1000; // 20s — overview-bundle KPIs
/** Serve stale data up to this multiple of TTL while revalidating in background. */
const STALE_MULTIPLIER = 4;

type CacheEntry = { data: any; timestamp: number; ttl: number };

const cache = new Map<string, CacheEntry>();
const inFlightGets = new Map<string, Promise<{ data: any }>>();
/** Bumped on invalidate so in-flight GETs cannot write a pre-mutation payload back. */
const cacheEpoch = new Map<string, number>();

function bumpCacheEpoch(urlPrefix?: string) {
  const keys: { [k: string]: true } = {};
  cache.forEach((_v, k) => {
    keys[k] = true;
  });
  inFlightGets.forEach((_v, k) => {
    keys[k] = true;
  });
  cacheEpoch.forEach((_v, k) => {
    keys[k] = true;
  });
  Object.keys(keys).forEach((k) => {
    if (!urlPrefix || k.indexOf(urlPrefix) !== -1) {
      cacheEpoch.set(k, (cacheEpoch.get(k) || 0) + 1);
    }
  });
}

const staticEndpoints = [
  '/inventory/materials',
  '/currencies',
  '/users',
  '/customers',
  '/logistics-config/labels',
];

const shortCachePrefixes = [
  '/inventory',
  '/dashboard/stats',
  '/dashboard/open-orders',
  '/dashboard/open-orders-summary',
  '/analysis/summary',
  '/orders',
  '/production',
  '/qc',
  '/payroll',
  '/leave',
  '/attendance',
  '/users',
];

const mutableListPrefixes = ['/expenses', '/procurements', '/vouchers', '/projects'];

const financeAggregatePaths = [
  '/expenses/summary',
  '/expenses/aggregates',
  '/expenses/overview-bundle',
  '/analysis/finance',
  '/analysis/campaigns',
];

const purchaseHubBundlePath = '/procurements/purchase-hub-bundle';

export const invalidateApiCache = (urlPrefix?: string) => {
  bumpCacheEpoch(urlPrefix);
  if (!urlPrefix) {
    cache.clear();
    inFlightGets.clear();
    return;
  }
  cache.forEach((_v, key) => {
    if (key.indexOf(urlPrefix) !== -1) cache.delete(key);
  });
  inFlightGets.forEach((_v, key) => {
    if (key.indexOf(urlPrefix) !== -1) inFlightGets.delete(key);
  });
};

/** Invalidate every cache prefix related to the given domains. */
export function invalidateDomains(domains: DataDomain[]): void {
  const all = domains.includes('all');
  if (all) {
    invalidateApiCache();
    return;
  }
  if (domains.includes('inventory') || domains.includes('production')) {
    invalidateApiCache('/inventory');
    invalidateApiCache('/sellable');
    invalidateApiCache('/vouchers');
  }
  if (domains.includes('purchases') || domains.includes('finance')) {
    invalidateApiCache('/procurements');
    invalidateApiCache('purchase-hub-bundle');
    invalidateApiCache('/expenses');
    invalidateApiCache('/expenses/summary');
    invalidateApiCache('/expenses/aggregates');
    invalidateApiCache('/expenses/overview-bundle');
    invalidateApiCache('/analysis/finance');
    invalidateApiCache('/analysis/campaigns');
    invalidateApiCache('/vouchers');
  }
  if (domains.includes('orders')) {
    invalidateApiCache('/orders');
    invalidateApiCache('/inventory');
    invalidateApiCache('/expenses');
  }
  if (domains.includes('payroll')) {
    invalidateApiCache('/payroll');
    invalidateApiCache('/attendance');
    invalidateApiCache('/expenses');
    invalidateApiCache('/analysis/finance');
    invalidateApiCache('/analysis/campaigns');
    invalidateApiCache('/leave');
    invalidateApiCache('/users');
  }
  if (domains.includes('dashboard')) {
    invalidateApiCache('/dashboard');
    invalidateApiCache('/analysis');
  }
  if (domains.includes('projects')) {
    invalidateApiCache('/analysis/campaigns');
  }
}

/**
 * Invalidate domain caches and notify mounted screens (soft real-time).
 * Prefer this after manual cache clears when the interceptor did not run.
 */
export function syncAfterMutation(domains: DataDomain[], source?: string): void {
  invalidateDomains(domains);
  notifyDataChanged(domains, source);
}

const pathOnly = (url: string) => url.split('?')[0].replace(/\/$/, '') || '/';

const noCachePaths = ['/currencies/preview', '/currencies/logs'];

export function getCacheTtl(url: string): number | null {
  const path = pathOnly(url);
  if (noCachePaths.some((p) => path === p || path.endsWith(p))) {
    return null;
  }
  if (financeAggregatePaths.some((p) => path === p || path.endsWith(p))) {
    return FINANCE_KPI_CACHE_DURATION;
  }
  if (path.includes('/expenses/documents/')) {
    return CACHE_DURATION;
  }
  if (path.includes(purchaseHubBundlePath)) {
    return PURCHASE_HUB_CACHE_DURATION;
  }
  if (mutableListPrefixes.some((p) => path === p || path.startsWith(p))) {
    if (url.includes('hub=1') || url.includes('hub=purchase')) {
      return PURCHASE_HUB_CACHE_DURATION;
    }
    return MUTABLE_LIST_CACHE_DURATION;
  }
  if (
    staticEndpoints.some((endpoint) => path === endpoint || path.endsWith(endpoint)) &&
    !url.includes('?')
  ) {
    return CACHE_DURATION;
  }
  // Live ledger / ATP never cached (must match post-inbound/sales bag+kg totals).
  if (url.includes('/sellable/') || url.includes('/ledger')) return null;
  if (shortCachePrefixes.some((p) => url.includes(p))) {
    return SHORT_CACHE_DURATION;
  }
  return null;
};

function storeCache(url: string, data: any, ttl: number) {
  cache.set(url, { data, timestamp: Date.now(), ttl });
}

function fetchAndCache(url: string, ttl: number | null, bypassHttpCache = false): Promise<{ data: any }> {
  if (!cacheEpoch.has(url)) cacheEpoch.set(url, 0);
  if (bypassHttpCache) {
    // Drop a pre-mutation in-flight GET so it cannot overwrite the post-dispatch snapshot.
    inFlightGets.delete(url);
    cacheEpoch.set(url, (cacheEpoch.get(url) || 0) + 1);
  }

  const existing = inFlightGets.get(url);
  if (existing && !bypassHttpCache) return existing;

  const epoch = cacheEpoch.get(url) || 0;
  const request = api.get(
    url,
    bypassHttpCache ? { headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' } } : undefined
  ).then((response) => {
    if ((cacheEpoch.get(url) || 0) !== epoch) {
      return response;
    }
    if (ttl != null) {
      storeCache(url, response.data, ttl);
    }
    return response;
  });

  inFlightGets.set(url, request);
  request.finally(() => {
    if (inFlightGets.get(url) === request) {
      inFlightGets.delete(url);
    }
  });

  return request;
}

/**
 * GET with short TTL cache + stale-while-revalidate.
 * - forceRefresh: bypass cache (after mutations).
 * - Otherwise: fresh hit returns immediately; expired-but-soft-stale returns immediately
 *   and revalidates in background so page navigations feel instant.
 */
export const cachedGet = async (url: string, forceRefresh = false) => {
  const ttl = getCacheTtl(url);

  if (forceRefresh) {
    cache.delete(url);
    inFlightGets.delete(url);
  }

  if (ttl != null && !forceRefresh) {
    const cached = cache.get(url);
    if (cached) {
      const age = Date.now() - cached.timestamp;
      if (age < cached.ttl) {
        return Promise.resolve({ data: cached.data });
      }
      // Soft-stale: show last data, refresh in background (does not block navigation).
      if (age < cached.ttl * STALE_MULTIPLIER) {
        void fetchAndCache(url, ttl).catch(() => undefined);
        return Promise.resolve({ data: cached.data });
      }
    }

    return fetchAndCache(url, ttl);
  }

  if (ttl != null) {
    return fetchAndCache(url, ttl, forceRefresh);
  }

  return api.get(url);
};

api.interceptors.response.use((response) => {
  const method = (response.config.method || 'get').toLowerCase();
  if (['post', 'put', 'patch', 'delete'].includes(method)) {
    const url = String(response.config.url || '');
    const domains = domainsForMutationUrl(url);
    if (domains.length > 0) {
      invalidateDomains(domains);
      // Defer event so the mutating component can finish local optimistic state first.
      void Promise.resolve().then(() => notifyDataChanged(domains, url));
    }
  }
  return response;
});

export default api;
